import React, { FC, useCallback } from 'react';
import { DonationFundItemType } from '../../../../utils/interpretItemType';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { OnClickForCashDialogFn } from '../../../ProductGrid/ProductGrid.types';
import { useCatalogContext } from '../../../../state/Catalog';
import { useCashFundDialogContext } from '../../../CashFundDialogProvider';
import { useStoredCuration } from '../../../../hooks/useStoredCuration';
import { CATEGORY_TITLE_TO_FUND_TYPE, CategoryTitleToFundTypeKey } from '../../../ProductGrid/ProductGrid.controller';
import { DeriveTelemetryPageArgs, deriveTelemetryPage } from '../../../../utils/deriveTelemetryPage';
import { useSearchTermQueryParam } from '../../../../utils/queryParams';
import { PagesForTelemetry } from '../../../../Catalog.telemetry.types';
import { CatalogItemTile } from '../../../../Catalog.types';
import { StyledButton, registryPrimaryButtonStyles } from '../BuyForm/BuyForm.styles';
import { useCatalogRegistryTranslations } from '../../../../Catalog.i18n';

type Props = {
  isAddingItem: boolean;
  selectedProduct: CatalogItemTile;
  disabled?: boolean;
};

export const CashfundAddToRegistryButton: FC<Props> = ({ isAddingItem, selectedProduct, disabled = false }) => {
  const { getAddProductTranslations } = useCatalogRegistryTranslations();
  const { addToRegistry } = getAddProductTranslations();
  const { eventId, selectedCategory, onAddItemToRegistryWithoutEvent } = useCatalogContext();
  const { openPreviewAndEditCatalogCashItemDialog, openAddCatalogCashFundDialog } = useCashFundDialogContext();
  const { getRelatedCuration } = useStoredCuration(null);
  const fundType = CATEGORY_TITLE_TO_FUND_TYPE[selectedCategory?.title as CategoryTitleToFundTypeKey];
  const searchTerm = useSearchTermQueryParam();
  const deriveTelemetryPageForProduct = useCallback(
    (args: DeriveTelemetryPageArgs): PagesForTelemetry => {
      return searchTerm ? 'searchResults' : deriveTelemetryPage(args);
    },
    [searchTerm],
  );

  const handleClickForCashDialog = useEventCallback<OnClickForCashDialogFn>(({ clickedProduct, isQuickAdd, itemPosition }) => {
    if (eventId) {
      if (isQuickAdd) {
        openAddCatalogCashFundDialog({ addMethodType: 'quick add', product: clickedProduct, itemPosition });
      } else {
        openPreviewAndEditCatalogCashItemDialog({ product: clickedProduct, itemPosition });
      }
    } else {
      const storedCuration = getRelatedCuration(clickedProduct);
      onAddItemToRegistryWithoutEvent?.({
        type: 'catalog',
        itemType: (clickedProduct.itemType || fundType) as DonationFundItemType,
        catalogLocator: null,
        curation: storedCuration,
        category: selectedCategory,
        product: clickedProduct,
        isCreateFromCustom: false,
        itemPosition,
        telemetryArgs: {
          method: isQuickAdd ? 'quick add' : 'pdp cta',
          page: deriveTelemetryPageForProduct({ view: 'productGrid', product: clickedProduct, selectedCategory }),
        },
      });
    }
  });

  const handleCashFundClick = () => {
    handleClickForCashDialog?.({
      clickedProduct: selectedProduct,
      isQuickAdd: false,
      itemPosition: 1,
    });
  };

  return (
    <StyledButton
      overrides={registryPrimaryButtonStyles}
      marginTop={6}
      onClick={handleCashFundClick}
      loading={isAddingItem}
      disabled={disabled}
    >
      {addToRegistry}
    </StyledButton>
  );
};
